import { lazy, Suspense, useEffect, useState } from "react";
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router";

import { ArrowDownTrayIcon } from "@heroicons/react/24/solid";

import DocumentFillAuto from "~/atoms/animations/DocumentFillAuto";
import Button from "~/atoms/buttons/Button";
import GatedButton from "~/atoms/buttons/GatedButton";
import Link from "~/atoms/Link";
import Meta from "~/atoms/Meta";
import SkeletonLoader from "~/atoms/SkeletonLoader";
import CompanyLogo from "~/components/CompanyLogo";
import SubscriptionModalWrapper from "~/components/Stripe/SubscriptionModalWrapper";
import StandardPage from "~/containers/StandardPage";
import useCreateFormSubmissionPreview from "~/hooks/formSubmissionPreview/useCreateFormSubmissionPreview";
import { flattenFormSubmission } from "~/hooks/formSubmissions/useGetSingleFormSubmissions";
import useClientRedirect from "~/hooks/useClientRedirect";
import useDelayedState from "~/hooks/useDelayAwait";
import useWindowSize from "~/hooks/useWindowSize";
import {
  getLocalStorageItem,
  removeLocalStorageItem,
  setLocalStorageItem,
} from "~/lib/localStorage";
import { createCollectionItem } from "~/lib/payload/collection";
import { getLoggedInUser } from "~/lib/payload/users";
import CreateAccountForm from "~/molecules/authForms/create-account";
import LoginForm from "~/molecules/authForms/login";
import Header from "~/molecules/headers/HeaderNoProfile";
import type { FormSubmission } from "~/types";
import { getPagesFromFields } from "~/util/formHelpers";
import { stringifyParams } from "~/util/queryString";
import { trackPreviewVisit } from "~/util/tracking";

import useTemplateData from "../../../useTemplateGetter";

const PdfViewer = lazy(() => import("~/components/PdfViewer"));

const PageFormPreview = () => {
  const params = useParams<{
    templateSlug: string;
    stateSlug?: string;
  }>();

  const { templateSlug, stateSlug } = params;

  const { isLoading: isTemplateLoading, data } = useTemplateData({
    templateSlug,
  });

  const formData = data?.formData;
  const metadata = data?.metadata;
  // const skipPaywall = formData && formData.free;
  const skipPaywall = false; // Temporarily disable skipping paywall for all forms

  const pathname = useLocation().pathname;
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const redirect = useClientRedirect();
  const [windowWidth] = useWindowSize();

  const [showLoginPage, setShowLoginPage] = useState(false);
  const [authForm, setAuthForm] = useState("create");
  const [successfullAccountCreation, setSuccessfullAccountCreation] =
    useState(false);
  const [documentId, setDocumentId] = useState<string | null | undefined>(
    undefined
  );
  const [isStripeModalOpen, setIsStripeModalOpen] = useState(false);

  const tempId = searchParams.get("tempId") || "";

  // Creates the document in the backend after account creation/login
  const createDocument = async () => {
    if (!formData) return;

    const tempFormState = getLocalStorageItem(tempId);

    const userRes = await getLoggedInUser();
    const organization =
      // @ts-expect-error: It works. let it be
      userRes?.data?.user?.organizationPermissions?.docs?.[0]?.organization;
    const organizationId =
      typeof organization !== "string" ? organization?.id : undefined;

    const stateToSave = flattenFormSubmission(
      tempFormState,
      formData.fields as []
    );

    const submissionRes = await createCollectionItem<FormSubmission>({
      collection: "form-submissions",
      data: {
        organization: organizationId,
        submission: stateToSave,
        template: formData?.templateId,
        status: "complete",
      },
    });

    removeLocalStorageItem(tempId);

    return submissionRes?.data?.document;
  };

  const templateId = formData ? formData.templateId : undefined;

  const pages = formData ? getPagesFromFields(formData.fields as []) : [];

  const {
    isLoading: isPdfLoading,
    canPreview,
    stepToRedirect,
    preview,
    error,
    isUsingCache,
  } = useCreateFormSubmissionPreview({
    tempId,
    templateId: templateId,
    pages,
    isSerialLoading: isTemplateLoading,
  });

  // Only trigger delay when not using cache and actually loading from API
  const { isDelayLoading } = useDelayedState(
    3000,
    !isUsingCache && !isPdfLoading
  );

  // Track the preview visit for analytics
  useEffect(() => {
    if (isTemplateLoading) return;
    trackPreviewVisit();
  }, [isTemplateLoading]);

  useEffect(() => {
    if (!successfullAccountCreation) return;

    window.history.pushState(null, "", window.location.href);

    const handlePopState = () => {
      window.history.pushState(null, "", window.location.href);
    };

    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, [successfullAccountCreation]);

  if (isTemplateLoading) {
    return null;
  }

  if (!templateSlug || !templateId) {
    return null;
  }

  if (!formData) {
    return null; // Prevent rendering while redirecting
  }

  let baseUrl = `/forms/${templateSlug}`;
  if (stateSlug) {
    baseUrl += `/${stateSlug}`;
  }

  if (!isPdfLoading && !canPreview && !stepToRedirect) {
    redirect(baseUrl);
    return null;
  }

  if (!isPdfLoading && !canPreview && stepToRedirect) {
    const redirectBase = pathname.replace(/\/preview\/.*/, "");

    redirect(
      redirectBase +
        stringifyParams({
          tempId: searchParams.get("tempId") || "",
          step: pages[stepToRedirect].name,
        })
    );

    return null;
  }

  const isLoading = isPdfLoading || isDelayLoading;

  return (
    <>
      <Meta
        title={metadata?.title}
        description={metadata?.description}
        image={metadata?.image}
      />
      <Header />
      <div id="CIO_message" />
      <main className="my-10 pb-14 [@media(max-height:830px)]:mt-5">
        <StandardPage>
          {isLoading ? (
            <div className="flex min-h-full flex-col gap-5 md:flex-row">
              <main className="order-2 grow md:order-1">
                <div className="mx-autosticky shadow-b-2.5 -shadow-spread-2 top-0 rounded-lg border border-gray-200 shadow-lg shadow-gray-200">
                  <div className="flex aspect-[1/1.37] h-full min-h-[calc(100%*1.37)] w-full flex-col items-center">
                    <div className="mx-auto mt-[30%] h-36 w-36">
                      <DocumentFillAuto animationDuration={5000} />
                    </div>
                    <p className="mt-4 text-center text-3xl font-bold text-gray-900">
                      Generating your document...
                    </p>
                  </div>
                </div>
              </main>
              <div className="min-h-full w-full shrink-0 bg-white md:order-2 md:w-100">
                <div className="md:sticky md:top-20 md:p-4 md:pt-0">
                  <div className="flex flex-col gap-4">
                    <SkeletonLoader width="100%" height="48px" />
                    <SkeletonLoader width="100%" height="48px" />
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div
              className="flex min-h-full flex-col gap-5 md:flex-row"
              key={windowWidth}
            >
              <main className="order-2 grow md:order-1">
                <div className="sticky top-0 mx-auto">
                  <div className="w-full">
                    {error && <p>{error}</p>}
                    <Suspense fallback={null}>
                      {preview && (
                        <PdfViewer
                          pdf={`data:application/pdf;base64,${preview}`}
                          infiniteScroll
                        />
                      )}
                    </Suspense>
                  </div>
                </div>
              </main>

              <div className="min-h-full w-full shrink-0 bg-white md:order-2 md:w-100">
                <div className="md:sticky md:top-20 md:p-4 md:pt-0">
                  {showLoginPage && !successfullAccountCreation && (
                    <>
                      <div className="flex w-full justify-center pb-4 [@media(max-height:1019px)]:hidden">
                        <CompanyLogo />
                      </div>
                      <div className="shadow-b-2.5 -shadow-spread-2 rounded-lg p-4 shadow-md shadow-gray-200 md:p-6 [@media(max-height:1019px)]:pt-4">
                        {authForm === "create" && (
                          <CreateAccountForm
                            type="landlord"
                            loginOnClick={() => setAuthForm("login")}
                            full
                            onSuccess={async () => {
                              const docId = await createDocument();

                              if (skipPaywall) {
                                navigate(
                                  `/forms/document-redirect?templateSlug=${templateSlug}&stateSlug=${stateSlug}&documentId=${docId}&download=true`
                                );
                              } else {
                                setDocumentId(
                                  typeof docId === "string"
                                    ? docId
                                    : docId || null
                                );
                                setLocalStorageItem(
                                  "documentPrePaywall",
                                  docId
                                );
                                setSuccessfullAccountCreation(true);
                                setIsStripeModalOpen(true);
                              }
                            }}
                          />
                        )}

                        {authForm === "login" && (
                          <>
                            <LoginForm
                              type="landlord"
                              createOnClick={() => setAuthForm("create")}
                              full
                              onSuccess={async () => {
                                const docId = await createDocument();

                                if (skipPaywall) {
                                  navigate(
                                    `/forms/document-redirect?templateSlug=${templateSlug}&stateSlug=${stateSlug}&documentId=${docId}&download=true`
                                  );
                                } else {
                                  const userRes = await getLoggedInUser();
                                  const organizationPermission =
                                    userRes?.data?.user?.organizationPermissions
                                      ?.docs?.[0];
                                  const organization =
                                    typeof organizationPermission === "string"
                                      ? undefined
                                      : organizationPermission?.organization;
                                  const hasSubscription =
                                    typeof organization === "string"
                                      ? false
                                      : organization?.subscription
                                          ?.grantsAccess;

                                  const finalDocId =
                                    typeof docId === "string"
                                      ? docId
                                      : docId || null;

                                  if (hasSubscription) {
                                    navigate(
                                      `/landlord/documents/${finalDocId}`
                                    );
                                  } else {
                                    setDocumentId(finalDocId);
                                    setLocalStorageItem(
                                      "documentPrePaywall",
                                      docId
                                    );
                                    setSuccessfullAccountCreation(true);
                                    setIsStripeModalOpen(true);
                                  }
                                }
                              }}
                            />
                          </>
                        )}
                      </div>
                      {authForm === "login" && (
                        <div className="mt-4 flex justify-center">
                          <Link
                            href="/forgot-password/"
                            target="_blank"
                            className="text-sm text-gray-500"
                          >
                            Forgot password?
                          </Link>
                        </div>
                      )}
                    </>
                  )}

                  {!showLoginPage && !successfullAccountCreation && (
                    <div className="flex flex-col gap-4">
                      <Button
                        startAdornment={<ArrowDownTrayIcon />}
                        fullWidth
                        onClick={() => setShowLoginPage(true)}
                      >
                        Download
                      </Button>
                    </div>
                  )}

                  {successfullAccountCreation && !skipPaywall && (
                    <div className="flex flex-col gap-4">
                      <GatedButton
                        documentId={documentId}
                        fullWidth
                        returnPath={`/forms/stripe-checkout-return?templateSlug=${templateSlug}&stateSlug=${stateSlug}&documentId=${documentId}`}
                        feature="download"
                      >
                        Download
                      </GatedButton>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </StandardPage>
      </main>

      <SubscriptionModalWrapper
        isOpen={isStripeModalOpen}
        onClose={() => setIsStripeModalOpen(false)}
        returnPath={`/forms/stripe-checkout-return?templateSlug=${templateSlug}&stateSlug=${stateSlug}&documentId=${documentId}`}
        documentId={documentId}
      />
    </>
  );
};

export default PageFormPreview;
